import {React} from "react"
import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { registerService } from "../services/registerService"
import { validationService } from "../utils/validation/registerValidation";

const initialFormData = {
  fullname: '',
  email: '',
  mobile: '',
  password: '',
  cnfpassword: '',
  dob: '',
  gender: '',
  grade: '',
  board: '',
  tmc: false
};

const useRegisterForm = () => {
    const [currentStep, setCurrentStep] = useState(1)
    const [formData, setFormData] = useState(initialFormData)
    const [errors, setErrors] = useState({})
    const [otpData, setOtpData] = useState({
        otp: '',
        otpSent: false,
        enteredOtp: '',
        mobileForOtp: ''
    })
    const [showPassword, setShowPassword] = useState({
        password1: false,
        password2: false
    })

    const [loadingStates, setLoadingStates] = useState({ // 
        stepNavigation: false,
        otpVerification: false,
        formSubmission: false
    })

    const isSubmittingRef = useRef(false);
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()

    const handleChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    }, [errors]);

    const validateStep = useCallback((step) => {
        const stepData = step === 3 ? { ...formData, enteredOtp: otpData.enteredOtp } : formData
        const stepErrors = validationService.validateStep(step, stepData)
        setErrors(stepErrors)
        return Object.keys(stepErrors).length === 0
    }, [formData, otpData.enteredOtp])

    const handleOtpChange = useCallback((value) => {
        setOtpData((prev) => ({ ...prev, enteredOtp: value }))
        if (errors.enteredOtp) {
            setErrors(prev => ({ ...prev, enteredOtp: '' }));
        }
    }, [errors.enteredOtp])

    const handleStepChange = useCallback(
        async (direction) => {
            if (isSubmittingRef.current) return;

            if (direction === 'next') {
                if (!validateStep(currentStep)) {
                    // console.log('Validation failed for step:', currentStep)
                    return
                }

                isSubmittingRef.current = true;
                setLoadingStates(prev => ({...prev, stepNavigation: true})) // 

                try {
                    // Special handling for step 1 (check email/mobile)
                    if (currentStep === 1) {
                        // ✅ Only check existing user if OTP hasn't been sent OR mobile has changed
                        const shouldCheckUser = !otpData.otpSent || otpData.mobileForOtp !== formData.mobile;
                        
                        if (shouldCheckUser) {
                            await registerService.checkExistingUser(formData)
                        }

                        // ✅ Only send OTP if not already sent for this mobile
                        const shouldSendOtp = !otpData.otpSent || otpData.mobileForOtp !== formData.mobile;    

                        if (shouldSendOtp) {
                            const otp = await registerService.sendOTP(formData.mobile);
                            
                            setOtpData((prev) => ({
                                ...prev,
                                otp: otp.toString(),
                                otpSent: true,
                                mobileForOtp: formData.mobile
                            }))
                            enqueueSnackbar('OTP sent successfully!', { variant: 'success' })
                        } else {
                            enqueueSnackbar('OTP already sent to your mobile', { variant: 'info' })
                        }
                    }
                    setCurrentStep((prev) => prev + 1)
                } catch (error) {
                    enqueueSnackbar(error.message, { variant: 'error' })
                } finally {
                    isSubmittingRef.current = false;
                    setLoadingStates(prev => ({...prev, stepNavigation: false})) // 
                }
            } else {
                setCurrentStep((prev) => prev - 1)
            }
        },
        [currentStep, formData, validateStep, enqueueSnackbar, otpData.otpSent, otpData.mobileForOtp]
    )

    const handleSubmit = useCallback(
        async () => {
            if (isSubmittingRef.current) return;
            
            if (!validateStep(3)) {
                enqueueSnackbar('Please enter a valid 4-digit OTP', {
                    variant: 'error',
                })
                return
            }

            if (otpData.enteredOtp !== otpData.otp) {
                enqueueSnackbar('Invalid OTP. Please try again.', { variant: 'error' })
                return
            }

            isSubmittingRef.current = true;
            setLoadingStates(prev => ({ ...prev, formSubmission: true })) // 
            
            try {
                await registerService.submitRegistration({
                    ...formData,
                    otp: otpData.enteredOtp,
                })

                await registerService.sendConfirmationEmail(
                    formData.email,
                    formData.fullname
                )

                enqueueSnackbar('Registration Successful', { variant: 'success' })
                setTimeout(() => {
                    navigate('/thank-you')
                }, 2000)
            } catch (error) {
                console.error('Registration error:', error)
                enqueueSnackbar(error.response?.data?.error || error.message, {
                    variant: 'error',
                })
            } finally {
                isSubmittingRef.current = false;
                setLoadingStates(prev => ({ ...prev, formSubmission: false })) // 
            }
        },
        [formData, otpData, navigate, enqueueSnackbar, validateStep]
    )

    const handleClickPasswordShowOff = (field) => {
        setShowPassword((prevState) => ({
            ...prevState,
            [field]: !prevState[field]
        }))
    }

    // ✅ Fixed: Correct resendOTP function
    const resendOTP = useCallback(async () => {
        if (isSubmittingRef.current) return;
        
        isSubmittingRef.current = true;
        setLoadingStates(prev => ({ ...prev, otpVerification: true })) // 
        
        try {
            const newOtp = await registerService.sendOTP(formData.mobile);
            setOtpData(prev => ({ 
                ...prev, 
                otp: newOtp.toString(),
                mobileForOtp: formData.mobile 
            }));
            enqueueSnackbar('New OTP sent successfully!', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to resend OTP. Please try again.', { variant: 'error' });
        } finally {
            isSubmittingRef.current = false;
            setLoadingStates(prev => ({ ...prev, otpVerification: false })) // 
        }
    }, [formData.mobile, enqueueSnackbar])

    // ✅ Fixed: Return loadingStates properly
    return {
        currentStep,
        formData,
        errors,
        loadingStates, 
        isLoading: Object.values(loadingStates).some(state => state),
        otpData,
        handleChange,
        handleSubmit,
        handleStepChange,
        handleOtpChange,
        resendOTP,
        showPassword,
        handleClickPasswordShowOff
    }
}

export { useRegisterForm }